import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { CategoryScore } from '../../graphql/types';
import './MaturitySpiderChart.css';

export interface SpiderChartCategory {
  id: string;
  name: string;
}

export interface MaturitySpiderChartProps {
  categoryScores: CategoryScore[];
  categories: SpiderChartCategory[];
  highlightedCategoryId?: string | null;
  size?: number;
}

const MAX_LEVEL = 6;
const LEVELS = 6;

// Colorblind-safe palette (Tableau 10)
const SECTOR_COLORS = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
];

function scoreToColor(score: number): string {
  // Red (1) → Yellow (3.5) → Green (6)
  const t = Math.max(0, Math.min(1, (score - 1) / (MAX_LEVEL - 1)));
  if (t < 0.5) {
    const u = t * 2;
    const r = 204;
    const g = Math.round(u * 204);
    return `rgb(${r},${g},0)`;
  }
  const u = (t - 0.5) * 2;
  const r = Math.round((1 - u) * 204);
  const g = 160;
  return `rgb(${r},${g},0)`;
}

export function MaturitySpiderChart({
  categoryScores,
  categories,
  highlightedCategoryId,
  size = 360,
}: MaturitySpiderChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || categories.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = 72;
    const radius = size / 2 - margin;
    const cx = size / 2;
    const cy = size / 2;
    const total = categories.length;
    const angleSlice = (Math.PI * 2) / total;

    const rScale = d3.scaleLinear().range([0, radius]).domain([0, MAX_LEVEL]);

    // Radial gradients — one per category sector color
    const defs = svg.append('defs');
    categories.forEach((cat, i) => {
      const color = SECTOR_COLORS[i % SECTOR_COLORS.length];
      const grad = defs
        .append('radialGradient')
        .attr('id', `sector-grad-${i}`)
        .attr('cx', '50%')
        .attr('cy', '50%')
        .attr('r', '50%');
      grad.append('stop').attr('offset', '0%').attr('stop-color', color).attr('stop-opacity', 0.18);
      grad
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', color)
        .attr('stop-opacity', 0.08);
      void cat;
    });

    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`);

    // Draw concentric level circles
    for (let level = 1; level <= LEVELS; level++) {
      g.append('circle')
        .attr('r', rScale(level))
        .attr('fill', 'none')
        .attr('stroke', 'var(--forge-theme-border, #e0e0e0)')
        .attr('stroke-width', level === MAX_LEVEL ? 1 : 0.5)
        .attr('stroke-dasharray', level < MAX_LEVEL ? '2,3' : null);

      g.append('text')
        .attr('x', 4)
        .attr('y', -rScale(level) + 1)
        .attr('dy', '0.35em')
        .attr('font-size', '9px')
        .attr('fill', 'var(--forge-theme-text-medium, #757575)')
        .text(String(level));
    }

    // Draw sector wedge fills (subtle background per axis)
    categories.forEach((cat, i) => {
      const startAngle = angleSlice * i - Math.PI / 2 - angleSlice / 2;
      const endAngle = startAngle + angleSlice;
      const arc = d3
        .arc<null>()
        .innerRadius(0)
        .outerRadius(radius)
        .startAngle(startAngle)
        .endAngle(endAngle);

      const isHighlighted = highlightedCategoryId === cat.id;
      g.append('path')
        .attr('d', arc(null))
        .attr(
          'fill',
          isHighlighted ? SECTOR_COLORS[i % SECTOR_COLORS.length] + '33' : `url(#sector-grad-${i})`
        )
        .attr('stroke', isHighlighted ? SECTOR_COLORS[i % SECTOR_COLORS.length] : 'none')
        .attr('stroke-width', isHighlighted ? 1 : 0);
    });

    // Draw axes and labels
    categories.forEach((cat, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x = rScale(MAX_LEVEL) * Math.cos(angle);
      const y = rScale(MAX_LEVEL) * Math.sin(angle);
      const color = SECTOR_COLORS[i % SECTOR_COLORS.length];
      const isHighlighted = highlightedCategoryId === cat.id;

      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', isHighlighted ? color : 'var(--forge-theme-border, #e0e0e0)')
        .attr('stroke-width', isHighlighted ? 1.5 : 0.7);

      const labelR = rScale(MAX_LEVEL) + 18;
      const lx = labelR * Math.cos(angle);
      const ly = labelR * Math.sin(angle);
      const label = cat.name.length > 16 ? cat.name.slice(0, 15) + '…' : cat.name;

      g.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('dy', '0.35em')
        .attr('text-anchor', lx < -2 ? 'end' : lx > 2 ? 'start' : 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', isHighlighted ? '600' : '400')
        .attr('fill', isHighlighted ? color : 'var(--forge-theme-text-primary, #212121)')
        .text(label);
    });

    // Build polygon from scores
    const scoreMap = new Map(categoryScores.map((s) => [s.categoryId, s.score]));
    const points = categories.map((cat, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const score = scoreMap.get(cat.id) ?? 0;
      return { x: rScale(score) * Math.cos(angle), y: rScale(score) * Math.sin(angle), score };
    });

    const avgScore =
      points.length > 0 ? points.reduce((sum, p) => sum + p.score, 0) / points.length : 0;
    const fillColor = scoreToColor(avgScore);

    // Polygon fill
    g.append('polygon')
      .attr('points', points.map((p) => `${p.x},${p.y}`).join(' '))
      .attr('fill', fillColor.replace('rgb(', 'rgba(').replace(')', ', 0.18)'))
      .attr('stroke', fillColor)
      .attr('stroke-width', 2)
      .attr('stroke-linejoin', 'round');

    // Data point dots with tooltips
    points.forEach((p, i) => {
      const cat = categories.at(i);
      if (!cat) return;
      const color = SECTOR_COLORS[i % SECTOR_COLORS.length];
      const dotG = g.append('g').attr('class', 'spider-dot');

      dotG
        .append('circle')
        .attr('cx', p.x)
        .attr('cy', p.y)
        .attr('r', 5)
        .attr('fill', 'white')
        .attr('stroke', color)
        .attr('stroke-width', 2);

      dotG.append('circle').attr('cx', p.x).attr('cy', p.y).attr('r', 3).attr('fill', color);

      dotG.append('title').text(`${cat.name}: ${p.score > 0 ? p.score.toFixed(1) : 'Not scored'}`);
    });
  }, [categoryScores, categories, highlightedCategoryId, size]);

  return (
    <div className="maturity-spider-chart">
      <svg
        ref={svgRef}
        width={size}
        height={size}
        aria-label="Maturity radar chart showing category scores"
        role="img"
      />
    </div>
  );
}
